/**
 * StringGridControls — the FULL per-string module-grid control set, shared by
 * the single Edit Label popup and the multi-select batch section (owner
 * request 2026-09-02: "editing a specific string is allowing all controls…
 * also enabled in batch string edits").
 *
 * Two modes, one field vocabulary (see StringOverride in domain/layout/types:
 * every field REPLACES its site default):
 *  - single: inputs show the string's EFFECTIVE value (override ?? default);
 *    overridden fields get an accent border; blanking a field returns it to
 *    the site default.
 *  - batch: inputs start blank with the site default as placeholder; the
 *    moment a value is committed it applies to every selected string and
 *    STAYS VISIBLE (owner feedback: blur-commit + self-clearing read as
 *    "nothing changes"). Untouched fields keep each string's own value.
 *
 * Commits are LIVE: every keystroke/spinner click that parses to a valid
 * value applies immediately (mid-typing states like "0." or "-" are held);
 * blur snaps a single-mode field back to its effective value if the text
 * was left invalid. Native spinners are kept — inputs stretch to the grid
 * column, so the old width-clipping problem is gone.
 */
import { useEffect, useRef, useState } from 'react';
import type { ModuleGridConfig, StringOverride } from '../../domain/layout/types';

interface Props {
  config: ModuleGridConfig;
  mode: 'single' | 'batch';
  /** single mode: the string's current override (if any) */
  override?: StringOverride;
  /** batch mode: the selected strings' overrides (undefined entries = no
   *  override) — lets fields SHOW what the selection already carries:
   *  a shared value renders like single mode, divergent values render as
   *  "mixed". Without this the owner re-typed an already-applied value and
   *  read the no-op as a broken control (2026-09-02). */
  selectionOverrides?: (StringOverride | undefined)[];
  /** single mode: entity rotation — the direction default */
  entityRotation?: number;
  /** Apply a partial override patch (single: merged into this string's
   *  override; batch: batchPatchStringOverrides on the selection). */
  onPatch: (patch: Partial<StringOverride>) => void;
  /** Clear override(s) entirely. Rendered when provided. */
  onClear?: () => void;
  clearLabel?: string;
}

const ADVANCED_FIELDS: (keyof StringOverride)[] = [
  'moduleWidth', 'moduleHeight', 'moduleGap', 'rowGap',
  'gridOffsetAlongM', 'gridOffsetPerpM', 'serpentine',
];

function NumField({
  effective, placeholder, overridden, title, step, min, clearOnBlank, onCommit,
}: {
  /** the value currently in force (single: override ?? default; batch: the
   *  selection's SHARED override value, if all strings agree) */
  effective?: number;
  placeholder: string;
  overridden: boolean;
  title: string;
  step: number;
  min?: number;
  /** blanking the field clears the override(s) back to the site default */
  clearOnBlank?: boolean;
  onCommit: (v: number | undefined) => void;
}) {
  const [text, setText] = useState(effective != null ? String(effective) : '');
  const focusedRef = useRef(false);

  // Adopt external changes (Reset, another surface editing the same string)
  // whenever the user isn't actively typing in this field.
  useEffect(() => {
    if (!focusedRef.current) setText(effective != null ? String(effective) : '');
  }, [effective]);

  const tryCommit = (raw: string, final: boolean) => {
    const t = raw.trim();
    if (t === '') {
      // blank ⇒ clear the override (only once editing is done, otherwise
      // clearing to retype would wipe the override mid-edit)
      if (final && clearOnBlank) onCommit(undefined);
      return;
    }
    if (/[.\-+e]$/i.test(t)) return; // mid-typing ("0.", "-", "1e")
    const v = parseFloat(t);
    if (isNaN(v)) return;
    if (min != null && v < min) return;
    onCommit(v);
  };

  return (
    <input
      type="number"
      className={`llg-num${overridden ? ' llg-num--overridden' : ''}`}
      title={title}
      step={step}
      min={min}
      placeholder={placeholder}
      value={text}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(e) => {
        setText(e.target.value);
        tryCommit(e.target.value, false);
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        tryCommit(e.target.value, true);
        // invalid leftovers snap back to the value in force; valid commits
        // get re-synced by the effect when the new effective value arrives
        setText(effective != null ? String(effective) : '');
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
    />
  );
}

export function StringGridControls({
  config, mode, override, selectionOverrides, entityRotation, onPatch, onClear, clearLabel,
}: Props) {
  const single = mode === 'single';

  // Batch: what does the selection ALREADY carry for a field? A value every
  // string shares renders like single mode; divergent values render "mixed".
  const batchField = (key: keyof StringOverride): { common?: number; anySet: boolean; mixed: boolean } => {
    const sel = selectionOverrides ?? [];
    const values = sel.map((o) => o?.[key] as number | undefined);
    const set = values.filter((v) => v !== undefined);
    if (set.length === 0) return { anySet: false, mixed: false };
    const allSame = set.length === sel.length && set.every((v) => v === set[0]);
    return allSame ? { common: set[0], anySet: true, mixed: false } : { anySet: true, mixed: true };
  };

  const hasAdvanced = single
    ? ADVANCED_FIELDS.some((f) => override?.[f] !== undefined)
    : ADVANCED_FIELDS.some((f) => batchField(f).anySet);
  const [showAdvanced, setShowAdvanced] = useState(hasAdvanced);

  const ov = <K extends keyof StringOverride>(k: K) => override?.[k];
  const currentDir = (ov('directionOverrideDeg') as number | undefined)
    ?? (single ? entityRotation ?? 0 : undefined);

  // Row gap only acts BETWEEN rows: dim it when the rows IN FORCE are 1.
  // Selection-aware — strings carrying rows overrides (e.g. rows:3) must
  // never see the dim; a mixed/unknown selection is left undimmed too.
  const rowsInForce = single
    ? (ov('rows') ?? config.rows)
    : (() => {
        const bf = batchField('rows');
        return bf.anySet ? bf.common : config.rows; // mixed ⇒ undefined
      })();
  const rowOnlyDim = rowsInForce !== undefined && rowsInForce <= 1;
  const rowOnlyTitle = rowOnlyDim
    ? ' — no effect while Rows is 1; set Rows ≥ 2 first'
    : '';

  const num = (
    key: keyof StringOverride, def: number, title: string, step: number, min?: number,
  ) => {
    const bf = single ? undefined : batchField(key);
    // def=NaN ⇒ the field has no single site default (direction: each
    // string's own rotation)
    const defLabel = Number.isNaN(def) ? "each string's own" : String(def);
    return (
      <NumField

        effective={single ? (ov(key) as number | undefined) ?? def : bf!.common}
        placeholder={bf?.mixed ? 'mixed' : Number.isNaN(def) ? '—' : String(def)}
        overridden={single ? ov(key) !== undefined : bf!.anySet}
        title={`${title} — ${single
          ? 'blank = site default'
          : bf!.mixed
            ? `selected strings differ; default ${defLabel}. Type to unify, blank to clear all back to default`
            : bf!.anySet
              ? `all selected strings override this; blank to clear back to default ${defLabel}`
              : `default ${defLabel}; untouched = keep each string's own`}`}
        step={step}
        min={min}
        clearOnBlank={single || bf!.anySet}
        onCommit={(v) => onPatch({ [key]: v })}
      />
    );
  };


  // Strings whose modules were dragged into place: pinned cells IGNORE every
  // grid field (the dragged position wins), so grid edits look broken until
  // the pins are released. Surface it and offer the release in batch (the
  // single popup already has Reset Positions).
  const pinnedCount = single ? 0 : (selectionOverrides ?? []).filter(
    (o) => o?.modulePositions && Object.keys(o.modulePositions).length > 0,
  ).length;

  return (
    <>
      {!single && <div className="llg-hint">Untouched fields keep each string&apos;s own value</div>}
      {!single && pinnedCount > 0 && (
        <div className="llg-warn">
          <span>
            ⚠ {pinnedCount} of the selected strings have hand-placed modules —
            pinned cells don&apos;t follow grid fields
          </span>
          <button
            type="button"
            className="llg-btn"
            title="Delete the hand-placed module positions on the selected strings so cells follow the grid fields again (other overrides are kept)"
            onClick={() => onPatch({ modulePositions: undefined })}
          >
            Release
          </button>
        </div>
      )}

      <div className="llg-grid">
        <span className="llg-lab">Count</span>
        {num('moduleCount', config.moduleCount, 'Modules in this string', 1, 1)}
        <span className="llg-lab">Rows</span>
        {num('rows', config.rows, 'Rows', 1, 1)}

        <span className="llg-lab">Dir &deg;</span>
        {single ? (
          <NumField

            effective={currentDir}
            placeholder=""
            overridden={ov('directionOverrideDeg') !== undefined}
            title="Grid direction (°) — replaces label rotation; site offset still adds"
            step={1}
            clearOnBlank
            onCommit={(v) => onPatch({ directionOverrideDeg: v })}
          />
        ) : (
          num('directionOverrideDeg', NaN, 'Grid direction (°)', 1)
        )}
        {single ? (
          <>
            <span className="llg-lab" />
            <div className="llg-pair">
              <button type="button" className="llg-btn" onClick={() => onPatch({ directionOverrideDeg: (currentDir ?? 0) - 90 })}>-90</button>
              <button type="button" className="llg-btn" onClick={() => onPatch({ directionOverrideDeg: (currentDir ?? 0) + 90 })}>+90</button>
            </div>
          </>
        ) : (
          <>
            <span className="llg-lab">Polarity</span>
            <div className="llg-pair">
              <button type="button" className="llg-btn" title="Flip polarity on all selected" onClick={() => onPatch({ polarityFlip: true })}>Flip</button>
              <button type="button" className="llg-btn" title="Restore site polarity on all selected" onClick={() => onPatch({ polarityFlip: false })}>Unflip</button>
            </div>
          </>
        )}

        {single && (
          <>
            <span className="llg-lab">Polarity</span>
            <label className="llg-check">
              <input
                type="checkbox"
                checked={ov('polarityFlip') ?? false}
                onChange={(e) => onPatch({ polarityFlip: e.target.checked })}
              />
              Flip
            </label>
            <span className="llg-lab" />
            <span />
          </>
        )}

        {showAdvanced && (
          <>
            <span className="llg-lab">W m</span>
            {num('moduleWidth', config.moduleWidth, 'Module width (m)', 0.05, 0.01)}
            <span className="llg-lab">H m</span>
            {num('moduleHeight', config.moduleHeight, 'Module height (m)', 0.05, 0.01)}

            <span className="llg-lab">Gap</span>
            {num('moduleGap', config.moduleGap, 'Gap between modules (m)', 0.01, 0)}
            <span className={`llg-lab${rowOnlyDim ? ' llg-dim' : ''}`} title={rowOnlyDim ? `Gap between rows (m)${rowOnlyTitle}` : undefined}>Row gap</span>
            <span className={rowOnlyDim ? 'llg-dim' : undefined}>
              {num('rowGap', config.rowGap, `Gap between rows (m)${rowOnlyTitle}`, 0.01, 0)}
            </span>

            <span className="llg-lab" title="Shift the grid along its direction (m)">Shift &#8741;</span>
            {num('gridOffsetAlongM', config.gridOffsetAlongM ?? 0, 'Grid shift along direction (m)', 0.1)}
            <span className="llg-lab" title="Shift the grid perpendicular to its direction (m)">Shift &#8869;</span>
            {num('gridOffsetPerpM', config.gridOffsetPerpM ?? 0, 'Grid shift perpendicular (m)', 0.1)}

            <span className="llg-lab" title="Serpentine numbering on even rows">Serpent.</span>
            {single ? (
              <label className="llg-check">
                <input
                  type="checkbox"
                  checked={ov('serpentine') ?? config.serpentine}
                  onChange={(e) => onPatch({
                    serpentine: e.target.checked === config.serpentine ? undefined : e.target.checked,
                  })}
                />
                On
              </label>
            ) : (
              <div className="llg-pair">
                <button type="button" className="llg-btn" title="Serpentine numbering ON" onClick={() => onPatch({ serpentine: true })}>On</button>
                <button type="button" className="llg-btn" title="Serpentine numbering OFF" onClick={() => onPatch({ serpentine: false })}>Off</button>
              </div>
            )}
            <span className="llg-lab" />
            <span />
          </>
        )}
      </div>

      <div className="llg-footer">
        <button
          type="button"
          className="llg-expander"
          onClick={() => setShowAdvanced((s) => !s)}
        >
          {showAdvanced ? '▴ Less' : '▾ More (size, gaps, shift)'}
        </button>
        {onClear && (
          <button type="button" className="llg-btn llg-btn--danger" onClick={onClear}>
            {clearLabel ?? 'Reset'}
          </button>
        )}
      </div>
    </>
  );
}
